import { forwardRef, useEffect, useRef, useState, useLayoutEffect } from 'react';
import { BREAK_POINT_FOR_MOBILE_VIEW, DESIGN_MODES, SUBHUB_APP_NAME } from '../../../../constants';
import { EnergyState } from 'store/slices/EnergySlice/types';
import AllPanelsToggle from '../../AllPanelsToggle';
import useScreenDims from 'hooks/useScreenDims';
import {
	AdjustPanelColorToggle,
	AnnualUsage,
	ChangePanelSelector,
	ElectricBillInput,
	Offset,
	Production,
	SaveDesignBtn,
	SystemSize
} from '../../PanelComponent';
import DesignModesSelector from 'components/ModeSelector';
import Badges from '../../Badges';
import NextSteps from '../../NextStep';
import ShowSetback from '../../SetbackToggle';
import EdgeLenghtToggle from '../../EdgeLengthToggle';
import Button from 'components/Basic/Button';
import { EditFacetModalBtnWrap } from 'components/ManualToolModal/style';
import { UseQueryResult } from 'react-query';
import FacetListButton from 'components/ManualToolModal/facetListBtn';
import ImageryDate from 'components/DisplayEnergy/ImageryDate';
import { ImageryIcon, SolarDataIcon } from 'assets/svg';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { toggleFullViewSidePanel } from 'store/slices/ToolSlice';
import { InteractionBlocker } from 'components/InteractionBlocker/interactionBlockerLayer';
import { DesktopSidePanel, DesktopViewFixed, DesktopViewWrap, DivSolarImagery, ModeToggleDiv, PanelDetailDiv, RightSidePanel } from './style';
import { useRefDispatch } from 'contexts/RefContext'; 
import CustomMapSelect from 'components/CustomMapSelect';
import { isInHouseSimulationMode } from '../../../../util';

interface SolarDesktopViewProps {
	saveStatus: { isLoading: boolean; isError: boolean };
	getPanelDetailsForOrg: UseQueryResult<any, unknown>;
	savePanels: () => void;
}

const SolarDesktopView = forwardRef<HTMLDivElement, SolarDesktopViewProps>(
	(props, ref) => {
		const { saveStatus, getPanelDetailsForOrg, savePanels } = props;
		const energy = useSelector((state: RootState) => state.energyData.data as unknown as EnergyState);
		const { uuid, roofSegsFromWhichUserHasSelectedPanels, panel, org, selectedPanelKey, mode,
			imageryCapturedDate, solarDataCapturedDate, roofDataChanging, autoSwitchStatus, billingLogs,
			modeAndItsCorrespondingImagery, roofDesignMode, panelsKeysForWhichDesignIsAvailable } = useSelector((state: RootState) => ({
			...state.roofData.data,
			roofDataChanging: state.roofData.configuringNewPanel || state.roofData.loading || state.roofData.switchingImageryProvider
		}));
		const { parentAppName, energyDerateRate, stateCode, showLidarDate: showLidarDateParam } =
			useSelector((state: RootState) => state.QueryParams.data);
		const showLidarDate = showLidarDateParam && !isInHouseSimulationMode(mode);
		const minPanelsRequiredForDesignToQualify = org.setting.minPanelsRequiredForDesignToQualify;
		const [error, setError] = useState<boolean>(false);
		const { roofIndexes, editModeEnabled, fullScreenEnabled, fullViewSidePanel } = useSelector((state: RootState) => state.toolNewPostions.data);
		const isParentNotEmbeddedInSubHub = parentAppName !== SUBHUB_APP_NAME;
		const someFacetIsStillInEditMode = roofSegsFromWhichUserHasSelectedPanels.filter(ind => roofIndexes.includes(ind));
		const disableOnEdit = editModeEnabled || !!someFacetIsStillInEditMode.length;
		const { width: screenWidth } = useScreenDims();
		const showMobileView = screenWidth <= BREAK_POINT_FOR_MOBILE_VIEW;
		const showAllPanelsToggle = isParentNotEmbeddedInSubHub && org.setting.showToggleForAllPanels;

		const isInvalidDesignToProceed = energy.selectedPanels?.length < minPanelsRequiredForDesignToQualify;
		const fullviewSideToggleClicked = useRef(false);
		const isFullScreenSideModelMinimized = fullScreenEnabled && !fullViewSidePanel;
		const isFullScreenSideModelMaximized = fullScreenEnabled && fullViewSidePanel;
		const dispatch = useDispatch();
		const refDispatch = useRefDispatch();
		const desktopViewWrapRef = useRef<HTMLDivElement | null>(null);

		const handleToggleMinimize = () => {
			fullviewSideToggleClicked.current = true;
			dispatch(toggleFullViewSidePanel({ enabled: !fullViewSidePanel }));
		};

		useEffect(() => {
			fullviewSideToggleClicked.current = false;
		}, [fullScreenEnabled]);

		useLayoutEffect(() => {
			refDispatch({ type: 'ADD_DESKTOP_VIEW_WRAP_REF', payload: { ref: desktopViewWrapRef } });
		}, [refDispatch]);

		return (
			<RightSidePanel
				ref={ref}
				isB2CView={org.setting.isB2C}
				className={`
					${fullScreenEnabled ? 'fullScreenView' : 'defaultScreenView'} 
					${fullScreenEnabled ? (fullViewSidePanel ? 'fullScreenMax' : 'fullScreenMin') : ''}
					`}
			>
				{isFullScreenSideModelMinimized &&
					<Button
						onClick={handleToggleMinimize}
						className="toggleModeFullscren toggleMinizedBtn"
					>
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
							<path fill="none" stroke="var(--defaultIconColor)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
								d="M7 7l5 5-5 5M13 7l5 5-5 5" />
						</svg>
					</Button>
				}
				<DesktopViewFixed 
					ref={desktopViewWrapRef}
					fullScreenEnabled={fullScreenEnabled} 
					isB2CView={org.setting.isB2C}
					className={`
					${fullScreenEnabled ? (fullViewSidePanel ? 'screenMax' : 'screenMini') : ''}
					${(fullScreenEnabled && !fullviewSideToggleClicked.current ? 'hideMinTansition' : '')}
					`}>
					<DesktopSidePanel
						className={`${fullScreenEnabled ? 'fullViewMode' : 'defaultScreenView'} 
						${fullScreenEnabled ? (fullViewSidePanel ? 'fullscreenViewMaximize' : 'fullScreenViewMinized') : ''}`}>
						{isFullScreenSideModelMaximized &&
							<ModeToggleDiv className="modeMaximize">
								<Button
									onClick={handleToggleMinimize}
									className="toggleModeFullscren"
								>
									<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
										<path fill="none" stroke="var(--defaultIconColor)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
											d="M17 7l-5 5 5 5M11 7l-5 5 5 5" />
									</svg>
								</Button>
							</ModeToggleDiv>
						}
						<InteractionBlocker>
							{(showAllPanelsToggle || !org.setting.isB2C) &&
								<DesktopViewWrap className={`${fullScreenEnabled ? 'fullscreeview-wrap' : 'screeview-wrap'}`}>
									{
										!org.setting.isB2C && (<>
											<DesignModesSelector
												designMode={mode}
												roofMode={roofDesignMode}
												designUUID={uuid}
												panelKey={panel.key}
												energyDerateRate={energyDerateRate}
												stateCode={stateCode}
												billingLogs={billingLogs}
												autoSwitchStatus={autoSwitchStatus}
												modeAndItsCorrespondingImagery={modeAndItsCorrespondingImagery}

											/>
											<EditFacetModalBtnWrap>
												<FacetListButton />
											</EditFacetModalBtnWrap>

											<DivSolarImagery className='solar-imagery-wrap'>
												<ImageryDate label="Imagery" date={imageryCapturedDate} 
													icon={<ImageryIcon width="38" height="38" />} />
												{showLidarDate && <ImageryDate label="Solar Data" date={solarDataCapturedDate} 
													icon={<SolarDataIcon width="38" height="38" />
													}/>}
											</DivSolarImagery>
										</>)
									}

									{showAllPanelsToggle && <AllPanelsToggle />}
								</DesktopViewWrap>}
							<DesktopViewWrap >
								<PanelDetailDiv fullScreenEnabled={fullScreenEnabled} className='paneldetail-fullscreen'>
									<SystemSize energy={energy} />
									<Production energy={energy} />
									<Offset energy={energy} />
									<ElectricBillInput setError={setError} />
									{org.setting.acceptsAnnualEnergy && <AnnualUsage setError={setError} isMobileView={showMobileView} />}
									<ChangePanelSelector
										getPanelDetailsForOrgData={getPanelDetailsForOrg.data}
										selectedPanelKey={selectedPanelKey}
										panelsKeysForWhichDesignIsAvailable={panelsKeysForWhichDesignIsAvailable}
									/>
									{ mode === DESIGN_MODES.NEARMAP_NREL_MANUAL && <CustomMapSelect/>}
									{!org.setting?.isB2C && (
										!editModeEnabled ?
											<AdjustPanelColorToggle />
											:
											<> 
												<AdjustPanelColorToggle />
												<ShowSetback />
												<EdgeLenghtToggle />
											</>
									)}
								</PanelDetailDiv>
								{org.setting?.isB2C && <AdjustPanelColorToggle isB2C={true} />}
								<SaveDesignBtn
									key='save-solar-design-desktop'
									isB2C={!!org?.setting?.isB2C}
									designUUID={uuid}
									disabled={error || disableOnEdit || roofDataChanging || isInvalidDesignToProceed}
									onClick={savePanels}
									tooltipContent={isInvalidDesignToProceed ? `Atleast ${minPanelsRequiredForDesignToQualify} ${minPanelsRequiredForDesignToQualify<2 ? 'panel is' : 'panels are'} required to proceed.` : ''}
									id="save-design-solar-desktp"

									status={saveStatus}
								/>
								<NextSteps />
								<Badges />
							</DesktopViewWrap>
						</InteractionBlocker>

					</DesktopSidePanel>
				</DesktopViewFixed>
			</RightSidePanel>
		);
	}
);

SolarDesktopView.displayName = 'SolarDesktopView';
export default SolarDesktopView;
